import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

// Fade-up-on-scroll, shared by every "University Press" page (first built
// for /find-work-v1). `delay` is in milliseconds.
export function useReveal(threshold = 0.14) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, shown];
}

export function Reveal({ children, delay = 0, className = '', as: Tag = 'div' }) {
  const [ref, shown] = useReveal();
  // This component is used site-wide (course landing, catalog, My Library),
  // and used to fade+translate on scroll unconditionally — the one place
  // that already handled prefers-reduced-motion (CourseManage's modal, via
  // this same framer-motion hook) never had that fix propagate here.
  const reduceMotion = useReducedMotion();
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: shown || reduceMotion ? 1 : 0,
        transform: shown || reduceMotion ? 'none' : 'translateY(22px)',
        transition: reduceMotion
          ? 'none'
          : `opacity 0.7s ${delay}ms cubic-bezier(0.22,1,0.36,1), transform 0.7s ${delay}ms cubic-bezier(0.22,1,0.36,1)`,
      }}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
